import React, { useState, useEffect } from 'react';
import { getWalletLedger } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Download, RefreshCw } from 'lucide-react';
import { formatDateTimeDDMMYYYY } from '../../utils/dateUtils';
import { toast } from 'sonner';

const Reports = () => {
  const { user } = useAuth();
  const [fromDate, setFromDate] = useState(new Date().toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const loadReport = async (page = 1) => {
    const token = sessionStorage.getItem('token');
    if (!token) {
      alert('Unable to authenticate. Please log in again.');
      return;
    }

    setLoading(true);
    try {
      const params = {
        fromDate: fromDate,
        toDate: toDate,
        page,
        pageSize: 50,
      };
      const res = await getWalletLedger(params);
      setData(res.data);
      setTotalPages(res.data?.totalPages || 1);
      setCurrentPage(page);
    } catch (err) {
      console.error('Failed to load wallet ledger report:', err);
      if (err.response?.status === 401) {
        alert('Session expired. Please log in again.');
      } else if (err.response?.status === 500) {
        alert(`Server error: ${err.response?.data?.message || 'Unknown error'}`);
      } else {
        alert('Failed to load report. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Auto-load on mount for today's data
    loadReport(1);
  }, []);

  const exportData = async () => {
    if (!data) {
      toast.error('No data to export. Please generate a report first.');
      return;
    }

    setExporting(true);
    toast.info('Preparing export for all pages...');

    try {
      const allTransactions = [];
      let page = 1;
      let totalPgs = 1;
      
      // We'll use a larger pageSize for export to minimize requests
      const baseParams = {
        fromDate: fromDate,
        toDate: toDate,
        pageSize: 100
      };

      while (page <= totalPgs) {
        const res = await getWalletLedger({ ...baseParams, page });
        const txns = res.data?.transactions || [];
        allTransactions.push(...txns);
        totalPgs = res.data?.totalPages || 1;
        page++;

        // Progress update
        if (page % 5 === 0) {
          toast.info(`Fetching data... (${allTransactions.length} records)`);
        }
      }

      if (allTransactions.length === 0) {
        toast.error('No transactions found to export.');
        return;
      }

      const headers = [
        'Transaction ID', 'Type', 'API', 'Reference ID', 'Description',
        'Base Amount', 'Charge Amount', 'GST Amount', 'Commission Amount', 'TDS Amount',
        'Net Amount', 'Opening Balance', 'Closing Balance', 'Date'
      ];

      const rows = allTransactions.map(t => [
        `"${t.transactionId || ''}"`,
        `"${t.transactionType || ''}"`,
        `"${t.apiCode || 'N/A'}"`,
        `"${t.referenceId || ''}"`,
        `"${(t.description || '').replace(/"/g, '""')}"`,
        t.baseAmount || 0,
        t.chargeAmount || 0,
        t.gstAmount || 0,
        t.commissionAmount || 0,
        t.tdsAmount || 0,
        t.netAmount || 0,
        t.openingBalance || 0,
        t.closingBalance || 0,
        formatDateTimeDDMMYYYY(t.createdAt)
      ]);

      const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `merchant-ledger-${fromDate}-${toDate}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Successfully exported ${allTransactions.length} records.`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="w-full bg-gray-50">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Reports</h1>
          <Button variant="outline" onClick={exportData} disabled={exporting}>
            {exporting ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            {exporting ? 'Exporting...' : 'Export CSV'}
          </Button>
        </div>

        <Card>
          <CardContent className="p-6">
            <div className="flex gap-4 items-end flex-wrap">
              <div className="space-y-2"><Label htmlFor="fromDate">From Date</Label><Input id="fromDate" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-auto" /></div>
              <div className="space-y-2"><Label htmlFor="toDate">To Date</Label><Input id="toDate" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="w-auto" /></div>
              <Button onClick={() => loadReport(1)} disabled={loading} className="px-6">{loading ? 'Loading...' : 'Generate Report'}</Button>
            </div>
          </CardContent>
        </Card>

        {loading && (
          <Card className="shadow-sm">
            <CardContent className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading report data...</p>
            </CardContent>
          </Card>
        )}

        {data && !loading && (
          <>
            {/* Stats Cards Row 1 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              {/* <Card className="shadow-sm border-blue-100 bg-white"><CardContent className="p-4"><p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-semibold">Opening Balance</p><p className="text-xl font-bold text-blue-700">₹{(data.openingBalance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p></CardContent></Card> */}
              
              <Card className="shadow-sm border-l-4 border-l-blue-500 hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Closing Balance</p>
                  <p className="text-2xl font-bold text-blue-800">₹{(data.closingBalance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                </CardContent>
              </Card>

              <Card className="shadow-sm border-l-4 border-l-green-500 hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Total Credits</p>
                  <p className="text-2xl font-bold text-green-700">₹{(data.totalCredits || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                </CardContent>
              </Card>

              <Card className="shadow-sm border-l-4 border-l-red-500 hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Total Debits</p>
                  <p className="text-2xl font-bold text-red-700">₹{(data.totalDebits || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                </CardContent>
              </Card>

              <Card className="shadow-sm border-l-4 border-l-gray-400 hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Transactions</p>
                  <p className="text-2xl font-bold text-gray-800">{data.totalTransactions || 0}</p>
                </CardContent>
              </Card>
            </div>

            {/* Stats Cards Row 2 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Card className="shadow-sm border-orange-200 bg-orange-50/20 hover:bg-orange-50/40 transition-colors">
                <CardContent className="p-4">
                  <p className="text-[10px] uppercase tracking-wider text-orange-800 mb-1 font-bold">Total Charges</p>
                  <p className="text-xl font-bold text-orange-700">₹{(data.totalCharges || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                </CardContent>
              </Card>

              <Card className="shadow-sm border-purple-200 bg-purple-50/20 hover:bg-purple-50/40 transition-colors">
                <CardContent className="p-4">
                  <p className="text-[10px] uppercase tracking-wider text-purple-800 mb-1 font-bold">Total GST</p>
                  <p className="text-xl font-bold text-purple-700">₹{(data.totalGst || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                </CardContent>
              </Card>

              <Card className="shadow-sm border-green-200 bg-green-50/20 hover:bg-green-50/40 transition-colors">
                <CardContent className="p-4">
                  <p className="text-[10px] uppercase tracking-wider text-green-800 mb-1 font-bold">Total Commission</p>
                  <p className="text-xl font-bold text-green-700">₹{(data.totalCommission || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                </CardContent>
              </Card>

              <Card className="shadow-sm border-red-200 bg-red-50/20 hover:bg-red-50/40 transition-colors">
                <CardContent className="p-4">
                  <p className="text-[10px] uppercase tracking-wider text-red-800 mb-1 font-bold">Total TDS</p>
                  <p className="text-xl font-bold text-red-700">₹{(data.totalTds || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                </CardContent>
              </Card>
            </div>



            <Card className="shadow-sm overflow-hidden">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Transactions</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {data.transactions && data.transactions.length > 0 ? (
                  <div className="overflow-auto w-full relative">
                    <Table className="min-w-[1200px] w-full">
                      <TableHeader className="bg-gray-50 sticky top-0 z-20" style={{ position: 'sticky', backgroundColor: '#f9fafb', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                        <TableRow>
                          <TableHead className="whitespace-nowrap px-3 py-2 text-xs font-medium text-gray-700 min-w-[100px]">Txn ID</TableHead>
                          <TableHead className="whitespace-nowrap px-3 py-2 text-xs font-medium text-gray-700 min-w-[70px]">Type</TableHead>
                          <TableHead className="whitespace-nowrap px-3 py-2 text-xs font-medium text-gray-700 min-w-[100px]">Reference ID</TableHead>
                          <TableHead className="whitespace-nowrap px-3 py-2 text-xs font-medium text-gray-700 min-w-[180px]">Description</TableHead>
                          <TableHead className="whitespace-nowrap px-3 py-2 text-xs font-medium text-gray-700 min-w-[70px]">API</TableHead>
                          <TableHead className="whitespace-nowrap px-3 py-2 text-xs font-medium text-gray-700 text-right min-w-[90px]">Base Amount</TableHead>
                          <TableHead className="whitespace-nowrap px-3 py-2 text-xs font-medium text-gray-700 text-right min-w-[100px]">Charge + GST</TableHead>
                          <TableHead className="whitespace-nowrap px-3 py-2 text-xs font-medium text-gray-700 text-right min-w-[110px]">Commission - TDS</TableHead>
                          <TableHead className="whitespace-nowrap px-3 py-2 text-xs font-medium text-gray-700 text-right min-w-[90px]">Net Amount</TableHead>
                          <TableHead className="whitespace-nowrap px-3 py-2 text-xs font-medium text-gray-700 text-right min-w-[110px]">Opening Balance</TableHead>
                          <TableHead className="whitespace-nowrap px-3 py-2 text-xs font-medium text-gray-700 text-right min-w-[110px]">Closing Balance</TableHead>
                          <TableHead className="whitespace-nowrap px-3 py-2 text-xs font-medium text-gray-700 min-w-[120px]">Date</TableHead>
                        </TableRow>
                      </TableHeader>
                  <TableBody>
                    {data.transactions?.map(t => {
                      const getTransactionTypeColor = (type) => {
                        switch (type) {
                          case 'CREDIT':
                            return 'bg-green-100 text-green-800 border-green-300';
                          case 'DEBIT':
                            return 'bg-red-100 text-red-800 border-red-300';
                          case 'REFUND':
                            return 'bg-blue-100 text-blue-800 border-blue-300';
                          case 'COMMISSION':
                            return 'bg-purple-100 text-purple-800 border-purple-300';
                          default:
                            return 'bg-gray-100 text-gray-800 border-gray-300';
                        }
                      };

                      const getAmountColor = (type) => {
                        switch (type) {
                          case 'CREDIT':
                            return 'text-green-600 font-semibold';
                          case 'DEBIT':
                            return 'text-red-600 font-semibold';
                          case 'REFUND':
                            return 'text-blue-600 font-semibold';
                          case 'COMMISSION':
                            return 'text-purple-600 font-semibold';
                          default:
                            return 'text-gray-600';
                        }
                      };

                      return (
                        <TableRow key={t.id} className="hover:bg-gray-50 border-b text-[13px]">
                          <TableCell className="font-mono text-[11px] px-3 py-2 whitespace-nowrap min-w-[100px] text-gray-500">{t.transactionId}</TableCell>
                          <TableCell className="px-3 py-2 whitespace-nowrap min-w-[70px]">
                            <Badge className={`${getTransactionTypeColor(t.transactionType)} border text-[10px] px-1.5 py-0 font-medium`}>
                              {t.transactionType}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono text-xs px-3 py-2 whitespace-nowrap min-w-[100px] text-gray-700 font-semibold">{t.referenceId || '-'}</TableCell>
                          <TableCell className="px-3 py-2 max-w-xs min-w-[180px]" title={t.description}>
                            <div className="truncate text-gray-600 font-medium">{t.description || '-'}</div>
                          </TableCell>
                          <TableCell className="px-3 py-2 whitespace-nowrap min-w-[70px]">
                            <Badge variant="outline" className="text-[10px] text-gray-500 border-gray-200 bg-gray-50/50">
                              {t.apiCode || 'N/A'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-medium px-3 py-2 whitespace-nowrap min-w-[90px]">
                            {t.baseAmount > 0 ? `₹${t.baseAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '-'}
                          </TableCell>
                          <TableCell className="text-right px-3 py-2 whitespace-nowrap min-w-[100px]">
                            {(t.chargeAmount > 0 || t.gstAmount > 0) ? (
                              <div className="flex flex-col">
                                <span className="text-orange-600">₹{t.chargeAmount.toFixed(2)}</span>
                                <span className="text-gray-400 text-[10px]">+ ₹{t.gstAmount.toFixed(2)}</span>
                              </div>
                            ) : '-'}
                          </TableCell>
                          <TableCell className="text-right px-3 py-2 whitespace-nowrap min-w-[110px]">
                            {(t.commissionAmount > 0 || t.tdsAmount > 0) ? (
                              <div className="flex flex-col">
                                <span className="text-green-600">₹{t.commissionAmount.toFixed(2)}</span>
                                <span className="text-red-400 text-[10px]">- ₹{t.tdsAmount.toFixed(2)}</span>
                              </div>
                            ) : '-'}
                          </TableCell>
                          <TableCell className={`text-right font-semibold px-3 py-2 whitespace-nowrap min-w-[90px] ${getAmountColor(t.transactionType)}`}>
                            {t.transactionType === 'CREDIT' || t.transactionType === 'REFUND' ? '+' : '-'}
                            ₹{Math.abs(t.netAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell className="text-right font-medium px-3 py-2 whitespace-nowrap min-w-[110px]">₹{t.openingBalance?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                          <TableCell className="text-right font-medium px-3 py-2 whitespace-nowrap min-w-[110px]">₹{t.closingBalance?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                          <TableCell className="text-sm text-gray-600 px-3 py-2 whitespace-nowrap min-w-[120px]">
                            {formatDateTimeDDMMYYYY(t.createdAt, false)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                    </div>
                  ) : (
                    <div className="p-8 text-center text-gray-500">
                      No transactions found for the selected period.
                    </div>
                  )}
              </CardContent>
            </Card>

            {data.transactions && data.transactions.length > 0 && totalPages > 0 && (
              <div className="flex justify-between items-center mt-6 px-2">
                <div className="text-sm text-gray-600">
                  Showing {((currentPage - 1) * 50) + 1}-{Math.min(currentPage * 50, data.totalRecords)} of {data.totalRecords} transactions
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => loadReport(currentPage - 1)}
                    disabled={currentPage === 1 || loading}
                  >
                    Previous
                  </Button>
                  {Array.from({ length: totalPages }, (_, i) => {
                    const p = i + 1;
                    if (totalPages <= 7 || p === 1 || p === totalPages || (p >= currentPage - 1 && p <= currentPage + 1)) {
                      return (
                        <Button
                          key={p}
                          variant={currentPage === p ? "default" : "outline"}
                          size="sm"
                          onClick={() => loadReport(p)}
                          disabled={loading}
                        >
                          {p}
                        </Button>
                      );
                    }
                    if (p === 2 && currentPage > 3) return <span key={p} className="px-1 text-gray-400">...</span>;
                    if (p === totalPages - 1 && currentPage < totalPages - 2) return <span key={p} className="px-1 text-gray-400">...</span>;
                    return null;
                  })}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => loadReport(currentPage + 1)}
                    disabled={currentPage === totalPages || loading}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Reports;